import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { PublicClientApplication, AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../AuthConfig';
import jwt_decode, { jwtDecode, JwtPayload } from "jwt-decode";

const msalInstance = new PublicClientApplication(msalConfig);

type AuthContextType = {
  user: AccountInfo | null;
  isInitialized: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  roles: string[];
  featureToggles: any[],
  setFeaturesToggles: Dispatch<SetStateAction<never[]>>;
};

interface MyJwtPayload extends JwtPayload {
  roles?: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [featureToggles, setFeaturesToggles] = useState([])

  useEffect(() => {
    const initMsal = async () => {
      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          const decodedToken = jwtDecode<MyJwtPayload>(response.accessToken);
          const subRoles = decodedToken?.roles ?? [];
          setRoles(subRoles);
          setUser(response.account);
        } else {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            const activeAccount = accounts[0];
            msalInstance.setActiveAccount(activeAccount);
            setUser(activeAccount);

            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: activeAccount,
              });

              const decodedToken = jwtDecode<MyJwtPayload>(tokenResponse.accessToken);
              const subRoles = decodedToken?.roles ?? [];
              setRoles(subRoles);
            } catch (tokenErr) {
              console.warn("Não foi possível adquirir token para roles:", tokenErr);
            }
          }
        }

      } catch (err) {
        console.error("Erro ao inicializar MSAL:", err);
      } finally {
        setIsInitialized(true);
      }
    };

    initMsal();
  }, []);


  const login = async () => {
    if (!isInitialized) return;

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);

      try {
        await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        return;
      } catch {
        msalInstance.loginRedirect(loginRequest);
      }
    } else {
      msalInstance.loginRedirect(loginRequest);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!isInitialized || !user) {
      console.warn("MSAL não inicializado ou usuário não logado");
      return null;
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: msalInstance.getActiveAccount()!,
      });

      return response.accessToken;
    } catch (err) {
      console.error("Erro ao adquirir token", err);
      return null;
    }
  };

  const logout = () => msalInstance.logoutRedirect();

  return (
    <AuthContext.Provider value={{ user, isInitialized, login, logout, getAccessToken, roles, featureToggles, setFeaturesToggles }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be usado dentro de um AuthProvider');
  return context;
};
